import React, { useMemo } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from './rn';
import { fontSize, spacing, type ThemeColors, radius } from '../theme';
import { useTheme } from '../theme/ThemeContext';

/**
 * D-410 — satır içi ve hata görünümü eylemleri için ortak, dokunulabilir buton.
 *
 * `PrimaryActionButton` (D-323) sayfanın tek ana eylemidir: kenardan kenara
 * uzanır ve 52 px taşır. Satır içindeki bağlantı eylemleri ve hata
 * görünümündeki "Yeniden dene" ise dar olmak zorundadır; bu yüzden her ekran
 * kendi küçük butonunu yeniden yazmıştı ve hepsi **44 pt'nin altına** düşmüştü:
 * bağlantı eylemleri 38 px, çoğu yeniden-dene `paddingVertical: 8` ile ~36 px,
 * Instagram'ınki hiç dolgu taşımadığı için yalnız metin yüksekliği kadardı.
 *
 * 44 pt, dokunma hedefi için işletim sistemi ve WCAG 2.5.5 asgarisidir; altına
 * inen hedef, elin titrediği ya da ekranın küçük olduğu durumlarda ıskalanır.
 * Buradaki `minHeight` bu yüzden değiştirilebilir bir stil değil, bileşenin
 * sözleşmesidir: `style` ile genişlik/hizalama geçilebilir, yükseklik
 * daraltılamaz — stil dizisinde `minHeight` en sonda uygulanır.
 */
export type CompactActionVariant = 'primary' | 'danger' | 'plain';

type Props = {
  label: string;
  onPress: () => void;
  variant?: CompactActionVariant;
  disabled?: boolean;
  loading?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

export const MIN_TOUCH_TARGET = 44;

export default function CompactActionButton({
  label, onPress, variant = 'primary', disabled = false, loading = false, style, testID,
}: Props) {
  const colors = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const inactive = disabled || loading;

  const spinnerColor = variant === 'primary' ? colors.white : colors.black;

  return (
    <TouchableOpacity
      testID={testID}
      // `style` genişlik ve hizalama için açıktır; `guaranteedTarget` en sonda
      // durur ki hiçbir çağrı yeri asgari yüksekliği yanlışlıkla ezemesin.
      style={[styles.base, styles[variant], style, styles.guaranteedTarget, inactive && styles.inactive]}
      disabled={inactive}
      onPress={() => { if (!inactive) onPress(); }}
      activeOpacity={0.7}
      accessible
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ disabled: inactive, busy: loading }}>
      {/* Yüklenirken etiket yerleşimde kalır; buton sarsılmaz (D-323 semantiği). */}
      <Text
        testID={testID ? `${testID}-label` : undefined}
        style={[styles.label, styles[`${variant}Label` as const], loading && styles.hiddenLabel]}
        numberOfLines={2}
        accessible={false}>
        {label}
      </Text>
      {loading && (
        <View
          style={styles.spinner}
          pointerEvents="none"
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants">
          <ActivityIndicator color={spinnerColor} accessible={false} />
        </View>
      )}
    </TouchableOpacity>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  base: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.control,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Yükseklik sözleşmesi; stil dizisinde en sonda uygulanır.
  guaranteedTarget: { minHeight: MIN_TOUCH_TARGET },
  primary: { backgroundColor: colors.black },
  danger: { borderWidth: StyleSheet.hairlineWidth, borderColor: '#e53935' },
  plain: { backgroundColor: 'transparent' },
  inactive: { opacity: 0.6 },
  label: { fontSize: fontSize.sm, fontWeight: '600', textAlign: 'center' },
  primaryLabel: { color: colors.white },
  dangerLabel: { color: '#e53935' },
  plainLabel: { color: colors.black },
  hiddenLabel: { opacity: 0 },
  spinner: { ...StyleSheet.absoluteFill, alignItems: 'center', justifyContent: 'center' },
});
